<script>
import Table from '@adminCompanies/components/table.vue'
import VueTagsInput from '@johmun/vue-tags-input'
import axios from 'axios'
import { debounce } from 'debounce'
import { OPTIONS as options } from '@events/constant'
import FormBuilder from '@events/components/event-form-builder.vue'

export default {
  name: 'EventSettings',
  components: { Table, VueTagsInput, FormBuilder },
  props: {
    event: {
      type: Object,
      required: true,
    },
    companyId: {
      type: Number,
      required: true,
    },
    eventId: {
      type: Number,
      required: true,
    },
  },
  data: () => ({
    eventDisplayLeaders: [],
    eventForm: {},
    eventLeader: '',
    eventLeaders: [],
    formBuilderVisible: false,
    options,
    renderTable: true,
  }),
  watch: {
    eventLeader: 'getEventLeaders',
  },
  created() {
    // Set default values on edit event case
    this.setEventForm()
  },
  methods: {
    setEventForm() {
      if (!this.event) return
      const { otherDetails } = this.event
      this.eventForm = otherDetails
    },
    showFormBuilder(value) {
      if (!value) return
      this.formBuilderVisible = true
    },
    onFormBuilderSave(payload) {
      this.eventForm.registrationFormFields = payload
    },
    onFormBuilderClose() {
      this.formBuilderVisible = false
    },
    forceTableToRerender() {
      this.renderTable = false
      this.$nextTick().then(() => {
        this.renderTable = true
      })
    },
    resetLeaders() {
      this.eventLeader = ''
      this.eventLeaders = []
      this.eventDisplayLeaders = []
    },
    updateEventLeaders(newLeaders) {
      this.eventDisplayLeaders = []
      this.eventLeaders = newLeaders
    },
    getEventLeaders: debounce(async function getEventLeaders() {
      if (!this.eventLeader) return

      return axios
        .get(`/api/v1/user/startingWith/${this.eventLeader}/companyId/0`)
        .then(({ data }) => {
          if (!data.length) return
          if (data instanceof Array) {
            this.eventDisplayLeaders = data.map(({ id, name }) => ({
              id,
              text: name,
            }))
          }
        })
    }, 500),
    onAddLeadersClick() {
      if (!this.eventLeaders.length) return

      const allLeaders = this.eventLeaders.map(({ id, text }) => ({
        id,
        name: text,
      }))

      return axios
        .post(`/api/v1/opportunity/${this.event.otherDetails.id}/addLeaders`, {
          allLeaders: JSON.stringify(allLeaders),
        })
        .then(({ data }) => {
          this.forceTableToRerender()
          this.resetLeaders()
          alert('You have succesfully added the selected leaders!')
        })
        .catch(() => alert('Something went wrong.'))
    },
    onSaveClick() {
      let eventForm = this.eventForm
      if (!+eventForm.hasRegistrationForm) {
        const { registrationFormFields, ...rest } = this.eventForm
        eventForm = rest
      }
      this.$emit('save', eventForm)
    },
    onSaveExitClick(payload) {
      let eventForm = this.eventForm
      if (!+eventForm.hasRegistrationForm) {
        const { registrationFormFields, ...rest } = this.eventForm
        eventForm = rest
      }
      this.$emit('saveExit', eventForm)
      this.$emit('cancel', payload)
    },
    onCancelClick(payload) {
      this.$emit('cancel', payload)
    },
    onCancelEventClick(payload) {
      this.$emit('cancelEvent', payload)
    },
    onDetachEventClick(payload) {
      this.$emit('detachEvent', payload)
    },
  },
}
</script>

<template>
  <div class="grid grid-cols-6 gap-10">
    <BaseFormGroup class="col-span-6" label="Event Leaders:">
      <div class="flex">
        <VueTagsInput
          v-model="eventLeader"
          class="flex-1 mr-5 "
          :tags="eventLeaders"
          :autocomplete-items="eventDisplayLeaders"
          :add-only-from-autocomplete="true"
          :add-on-blur="false"
          placeholder="Search for individual users"
          @tags-changed="updateEventLeaders"
        />

        <button
          :class="{ 'bg-gray text-gray': !eventLeaders.length }"
          class="button primary-button h-8 leading-relax text-sml rounded-small-button px-3.5"
          @click="onAddLeadersClick"
        >
          Add selected users as event leader
        </button>
      </div>
    </BaseFormGroup>

    <BaseFormGroup class="col-span-6">
      <hr class="border-b border-gray-light" />

      <Table
        v-if="renderTable"
        :company-id="companyId"
        table-type="LEADERS"
        :event-id="eventId"
        no-edit-btn
        no-new-btn
      />
    </BaseFormGroup>

    <BaseFormGroup class="col-span-2" label="Registration Form:">
      <BaseSelectBox
        v-model.number="eventForm.hasRegistrationForm"
        label="Enable Registration Form?"
        :options="options"
      />

      <template v-slot:extra-label>
        <button
          :disabled="!eventForm.hasRegistrationForm"
          class="button primary-button leading-relax text-sml rounded-small-button px-3.5"
          @click="showFormBuilder(true)"
        >
          edit
        </button>
      </template>

      <FormBuilder
        v-if="formBuilderVisible"
        :registration-form-fields="eventForm.registrationFormFields"
        @save="onFormBuilderSave"
        @close="onFormBuilderClose"
      />
    </BaseFormGroup>

    <BaseFormGroup class="col-span-2" label="Guest Registration:">
      <BaseSelectBox
        v-model.number="eventForm.hasGuestsEnabled"
        label="Allow users to bring guests?"
        :options="options"
      />
    </BaseFormGroup>

    <BaseFormGroup class="col-span-2" label="Waitlist:">
      <BaseSelectBox
        v-model.number="eventForm.waitlist"
        label="Enable Waitlist?"
        :options="options"
      />
    </BaseFormGroup>

    <div class="col-span-4">
      <button
        class="button primary-button h-7.5 leading-relax text-sml rounded-small-button px-3"
        @click="onSaveClick"
      >
        Save & Continue
      </button>

      <button
        class="button primary-button h-7.5 leading-relax text-sml rounded-small-button px-3 ml-3"
        @click="onSaveExitClick"
      >
        Save & Exit
      </button>

      <button
        class="button secondary-button h-7.5 leading-relax text-sml rounded-small-button px-3"
        @click="onCancelClick"
      >
        Cancel
      </button>
    </div>
    <div class="col-span-2 text-right">
      <button
        class="button bg-red-500 h-7.5 leading-relax text-sml rounded-small-button px-3 text-white"
        @click="onCancelEventClick"
      >
        Cancel Volunteer Opportunity
      </button>
      <button
        v-if="event.otherDetails.recurringEnabled"
        class="button bg-blue-darker h-7.5 leading-relax text-sml rounded-small-button px-3 text-white ml-3"
        @click="onDetachEventClick"
      >
        Detach Volunteer Opportunity
      </button>
    </div>
  </div>
</template>
